import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    getAttendance,
    saveAttendance
} from "../api/attendanceApi.js";

export default function EmployeeAttendance() {

    const [attendanceDate, setAttendanceDate] =
        useState(
            new Date()
                .toISOString()
                .split("T")[0]
        );

    const [employees, setEmployees] =
        useState([]);

    const [isSubmitted, setIsSubmitted] =
        useState(false);

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const loadAttendance = useCallback(async () => {

        try {

            setLoading(true);

            const response =
                await getAttendance(
                    attendanceDate
                );

            setEmployees(
                response.data || []
            );

            setIsSubmitted(
                response.isSubmitted || false
            );

        } catch (error) {

            console.error(
                "loadAttendance:",
                error
            );

            alert(
                error?.response?.data?.message ||
                "Unable to load attendance"
            );
        } finally {

            setLoading(false);
        }
    }, [attendanceDate]);

    useEffect(() => {
        loadAttendance();
    }, [loadAttendance]);

    const updateStatus = (
        uid,
        status
    ) => {

        setEmployees(prev =>
            prev.map(emp =>
                emp.uid === uid
                    ? {
                        ...emp,
                        status
                    }
                    : emp
            )
        );
    };

    const updateField = (
        uid,
        field,
        value
    ) => {

        setEmployees(prev =>
            prev.map(emp =>
                emp.uid === uid
                    ? {
                        ...emp,
                        [field]: value
                    }
                    : emp
            )
        );
    };

    const markAllPresent = () => {

        setEmployees(prev =>
            prev.map(emp => ({
                ...emp,
                status: "present"
            }))
        );
    };


    const handleSave = async () => {

        if (!attendanceDate) {

            alert("Attendance date is required");
            return;
        }

        if (employees.length === 0) {

            alert("No employees available for attendance");
            return;
        }

        const unsubmittedEmployees = employees.filter(
            (emp) => !emp.status || emp.status.trim() === ""
        );

        if (unsubmittedEmployees.length > 0) {
            const names = unsubmittedEmployees
                .map(emp => `${emp.firstname} ${emp.lastname}`)
                .join(", ");
            alert(
                `Please mark attendance for the following employees: ${names}`
            );
            return;
        }

        try {

            setSaving(true);

            await saveAttendance({
                attendanceDate,
                employees
            });

            alert(
                "Attendance saved successfully"
            );

            await loadAttendance();

        } catch (error) {

            alert(
                error?.response?.data?.message ||
                "Unable to save attendance"
            );

        } finally {

            setSaving(false);
        }
    }

    const presentCount = useMemo(
        () =>
            employees.filter(
                e => e.status === "present"
            ).length,
        [employees]
    );

    const absentCount = useMemo(
        () =>
            employees.filter(
                e => e.status === "absent"
            ).length,
        [employees]
    );

    const halfDayCount = useMemo(
        () =>
            employees.filter(
                e => e.status === "halfday"
            ).length,
        [employees]
    );

    const leaveCount = useMemo(
        () =>
            employees.filter(
                e => e.status === "leave"
            ).length,
        [employees]
    );

    return (
        <div className="min-h-screen bg-[var(--app-bg)] text-[var(--text)] p-6">

            {/* Header */}
            <div className="flex justify-between items-center mb-6">

                <div>
                    <h1 className="text-3xl font-bold text-[var(--text)]">
                        Employee Attendance
                    </h1>

                    <p className="text-[var(--text-muted)]">
                        Daily Attendance Register
                    </p>
                </div>

                <button
                    onClick={markAllPresent}
                    className="
                    bg-green-600
                    text-white
                    px-4
                    py-2
                    rounded-xl
                "
                >
                    Mark All Present
                </button>
            </div>
            {/* Filters */}
            <div className="bg-[var(--surface-alt)] border border-[var(--border)] rounded-2xl shadow p-5 mb-6">

                <div className="grid md:grid-cols-3 gap-4">

                    <div>
                        <label className="block mb-2 font-medium text-[var(--text-muted)]">
                            Attendance Date
                        </label>

                        <input
                            type="date"
                            value={attendanceDate}
                            onChange={(e) =>
                                setAttendanceDate(
                                    e.target.value
                                )
                            }
                            className="w-full border border-[var(--border)] rounded-xl bg-[var(--input-bg)] text-[var(--text)] px-4 py-2"
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>
            {isSubmitted && (
                <div className="mb-6 rounded-2xl border border-yellow-500 bg-yellow-500/10 p-4 text-sm text-yellow-200">
                    Attendance has already been submitted for this date.
                </div>
            )}
            { /** Summary */}
            <div className="grid md:grid-cols-4 gap-4 mb-6">

                <div className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 rounded-2xl shadow border-l-4 border-green-500">
                    <h3 className="text-[var(--text-muted)]">
                        Present
                    </h3>

                    <p className="text-2xl font-bold text-green-600">
                        {presentCount}
                    </p>
                </div>

                <div className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 rounded-2xl shadow border-l-4 border-red-500">
                    <h3 className="text-[var(--text-muted)]">
                        Absent
                    </h3>

                    <p className="text-2xl font-bold text-red-600">
                        {absentCount}
                    </p>
                </div>

                <div className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 rounded-2xl shadow border-l-4 border-orange-500">
                    <h3 className="text-[var(--text-muted)]">
                        Half Day
                    </h3>

                    <p className="text-2xl font-bold text-orange-600">
                        {halfDayCount}
                    </p>
                </div>
                <div className="bg-[var(--surface-alt)] border border-[var(--border)] p-3 rounded-2xl shadow border-l-4 border-blue-500">
                    <h3 className="text-[var(--text-muted)]">
                        Leave
                    </h3>

                    <p className="text-2xl font-bold text-blue-600">
                        {leaveCount}
                    </p>
                </div>

            </div>

            {/* Table */}
            <div className="bg-[var(--surface-alt)] border border-[var(--border)] rounded-2xl shadow overflow-hidden">

                <div className="px-6 py-4 border-b border-[var(--border)]">
                    <h3 className="text-lg font-semibold">
                        Attendance Register
                    </h3>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-[var(--text)]">
                        <thead className="bg-[var(--surface-soft)] text-[var(--text)]">
                            <tr>
                                <th className="px-4 py-3 text-left">
                                    Employee
                                </th>
                                <th className="px-4 py-3 text-left">
                                    Mobile
                                </th>

                                <th className="px-4 py-3 text-left">
                                    Salary/Day
                                </th>

                                <th className="px-4 py-3 text-center">
                                    Present
                                </th>

                                <th className="px-4 py-3 text-center">
                                    Half Day
                                </th>

                                <th className="px-4 py-3 text-center">
                                    Leave
                                </th>

                                <th className="px-4 py-3 text-center">
                                    Absent
                                </th>

                                <th className="px-4 py-3 text-center">
                                    Check In
                                </th>
                                <th className="px-4 py-3 text-center">
                                    Check Out
                                </th>
                                <th className="px-4 py-3 text-center">
                                    Remarks
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.map(employee => (
                                <tr
                                    key={employee.uid}
                                    className={`border-t border-[var(--border)] text-[var(--text)] transition-colors duration-200 ${employee.status === "present"
                                            ? "bg-green-500/10"
                                            : employee.status === "halfday"
                                                ? "bg-orange-500/10"
                                                : employee.status === "leave"
                                                    ? "bg-blue-500/10"
                                                    : employee.status === "absent"
                                                        ? "bg-red-500/10"
                                                        : ""
                                        }`}
                                >
                                    <td className="px-4 py-4">
                                        {employee.employee_name}
                                    </td>

                                    <td className="px-4 py-4">
                                        {employee.phone || "-"}
                                    </td>

                                    <td className="px-4 py-4">
                                        -
                                    </td>

                                    <td className="text-center">
                                        <input
                                            type="radio"
                                            name={`attendance-${employee.uid}`}
                                            checked={
                                                employee.status ===
                                                "present"
                                            }
                                            onChange={() =>
                                                updateStatus(
                                                    employee.uid,
                                                    "present"
                                                )
                                            }
                                        />
                                    </td>

                                    <td className="text-center">
                                        <input
                                            type="radio"
                                            name={`attendance-${employee.uid}`}
                                            checked={
                                                employee.status ===
                                                "halfday"
                                            }
                                            onChange={() =>
                                                updateStatus(
                                                    employee.uid,
                                                    "halfday"
                                                )
                                            }
                                        />
                                    </td>

                                    <td className="text-center">
                                        <input
                                            type="radio"
                                            name={`attendance-${employee.uid}`}
                                            checked={
                                                employee.status ===
                                                "leave"
                                            }
                                            onChange={() =>
                                                updateStatus(
                                                    employee.uid,
                                                    "leave"
                                                )
                                            }
                                        />
                                    </td>

                                    <td className="text-center">
                                        <input
                                            type="radio"
                                            name={`attendance-${employee.uid}`}
                                            checked={
                                                employee.status ===
                                                "absent"
                                            }
                                            onChange={() =>
                                                updateStatus(
                                                    employee.uid,
                                                    "absent"
                                                )
                                            }
                                        />
                                    </td>

                                    <td className="px-4 py-3">
                                        <input
                                            type="time"
                                            value={employee.checkin || ""}
                                            onChange={(e) =>
                                                updateField(
                                                    employee.uid,
                                                    "checkin",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-[var(--border)] rounded-lg bg-[var(--input-bg)] text-[var(--text)] px-2 py-1 w-full"
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <input
                                            type="time"
                                            value={employee.checkout || ""}
                                            onChange={(e) =>
                                                updateField(
                                                    employee.uid,
                                                    "checkout",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-[var(--border)] rounded-lg bg-[var(--input-bg)] text-[var(--text)] px-2 py-1 w-full"
                                        />
                                    </td>

                                    <td className="px-4 py-3">
                                        <input
                                            type="text"
                                            value={employee.remarks || ""}
                                            placeholder="Remarks"
                                            onChange={(e) =>
                                                updateField(
                                                    employee.uid,
                                                    "remarks",
                                                    e.target.value
                                                )
                                            }
                                            className="border border-[var(--border)] rounded-lg bg-[var(--input-bg)] text-[var(--text)] px-2 py-1 w-full"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="p-5 border-t flex justify-end">
                    <button
                        onClick={handleSave}
                        disabled={saving || loading || isSubmitted}
                        className="
                            bg-indigo-600
                            hover:bg-indigo-700
                            text-white
                            px-6
                            py-3
                            rounded-xl
                            disabled:cursor-not-allowed
                            disabled:bg-[var(--surface-soft)]
                        "
                    >
                        {saving
                            ? "Saving..."
                            : loading
                                ? "Loading..."
                                : isSubmitted
                                    ? "Already Submitted"
                                    : "Save Attendance"}
                    </button>
                </div>
            </div>
        </div>
    );
}